import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader } from '@/components/enterprise';
import {
  supplierInvoiceService,
  type SupplierInvoiceDTO,
} from '@/services/supplierInvoice.service';

const { Text } = Typography;

const matchColors: Record<string, string> = {
  Pending: 'gold',
  Matched: 'green',
  Variance: 'orange',
  Hold: 'red',
  NotApplicable: 'default',
};

const ThreeWayMatchingPage: React.FC = () => {
  const { t } = useTranslation('procurement');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');

  const matchStatusLabel = (v?: string | null) => {
    const map: Record<string, string> = {
      Pending: t('statusPending'),
      Matched: t('statusMatched'),
      Variance: t('statusVariance'),
      Hold: t('statusHold'),
      NotApplicable: t('statusNotApplicable'),
    };
    return map[v || 'Pending'] ?? v ?? t('statusPending');
  };

  const invoiceStatusLabel = (s: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Submitted: t('statusSubmitted'),
      Approved: t('statusApproved'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[s] ?? s;
  };

  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ['si-matching-queue', status, search],
    queryFn: () =>
      supplierInvoiceService.getMatchingQueue({
        matching_status: status || undefined,
        search: search || undefined,
      }),
  });

  const columns = useMemo(
    () => [
      {
        title: t('billNo'),
        dataIndex: 'invoice_number',
        key: 'invoice_number',
        render: (v: string, row: SupplierInvoiceDTO) => (
          <Button type="link" onClick={() => navigate(`/scm/procurement/supplier-invoices/${row.id}`)}>
            {v}
          </Button>
        ),
      },
      {
        title: t('supplierInvoiceNo'),
        dataIndex: 'supplier_invoice_number',
        key: 'supplier_invoice_number',
      },
      {
        title: t('supplier'),
        key: 'supplier',
        render: (_: unknown, row: SupplierInvoiceDTO) =>
          row.supplier_name_snapshot || row.supplier?.supplier_name || '—',
      },
      {
        title: t('date'),
        dataIndex: 'supplier_invoice_date',
        key: 'supplier_invoice_date',
        render: (v: string) => (v ? dayjs(v).format('DD-MMM-YYYY') : '—'),
      },
      {
        title: tc('status'),
        dataIndex: 'invoice_status',
        key: 'invoice_status',
        render: (v: string) => invoiceStatusLabel(v),
      },
      {
        title: t('match'),
        dataIndex: 'matching_status',
        key: 'matching_status',
        render: (v: string) => <Tag color={matchColors[v] || 'default'}>{matchStatusLabel(v)}</Tag>,
      },
      {
        title: t('variance'),
        dataIndex: 'variance_amount',
        key: 'variance_amount',
        align: 'right' as const,
        render: (v: number) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      },
      {
        title: t('total'),
        dataIndex: 'grand_total',
        key: 'grand_total',
        align: 'right' as const,
        render: (v: number) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }),
      },
    ],
    [navigate, t, tc]
  );

  return (
    <div style={{ padding: 24 }}>
      <EnterprisePageHeader
        documentLabel={t('threeWayMatching')}
        subtitle={t('threeWayMatchingSubtitle')}
      />
      <Card style={{ marginTop: 16 }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            placeholder={t('searchMatchingPlaceholder')}
            style={{ width: 280 }}
            onSearch={(v) => setSearch(v.trim())}
          />
          <Select
            allowClear
            placeholder={t('matchStatus')}
            style={{ width: 160 }}
            value={status || undefined}
            onChange={(v) => setStatus(v || '')}
            options={[
              { value: 'Pending', label: t('matchStatusPending') },
              { value: 'Variance', label: t('matchStatusVariance') },
              { value: 'Hold', label: t('matchStatusHold') },
            ]}
          />
          <Button onClick={() => refetch()}>{tc('refresh')}</Button>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('paymentBlockedUntilMatchedQueue')}
        </Text>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={data}
          columns={columns}
          pagination={{ pageSize: 25, showTotal: (count) => t('billsCount', { count }) }}
        />
      </Card>
    </div>
  );
};

export default ThreeWayMatchingPage;
